/**
 * Kichik URL-router.
 *
 * TZ §10.6: "Filtr holati URL'da saqlanadi (ulashish uchun)" — shuning
 * uchun butun holat query-parametrlarda turadi va react-router kabi
 * qo'shimcha bog'liqlik kerak emas.
 */

import { useEffect, useState } from "react";
import type { SearchQuery } from "./api";

export type Route =
  | { name: "home" }
  | { name: "onboarding" }
  | { name: "saved" }
  | { name: "search"; query: SearchQuery }
  | { name: "detail"; id: number; source?: string };

const BOOL_KEYS = ["no_ielts", "no_experience", "no_fee"] as const;
const STR_KEYS = [
  "q", "type", "degree", "funding", "country", "continent", "field",
  "format",
] as const;

export function parseRoute(search: string): Route {
  const params = new URLSearchParams(search);
  const screen = params.get("screen");

  if (screen === "onboarding") return { name: "onboarding" };
  if (screen === "saved") return { name: "saved" };

  if (screen === "opp") {
    const id = Number(params.get("id"));
    if (Number.isInteger(id) && id > 0) {
      const source = params.get("source");
      return source ? { name: "detail", id, source } : { name: "detail", id };
    }
    return { name: "home" };
  }

  if (screen === "search") {
    const query: SearchQuery = {};
    for (const key of STR_KEYS) {
      const value = params.get(key);
      if (value) query[key] = value;
    }
    for (const key of BOOL_KEYS) {
      if (params.get(key) === "true") query[key] = true;
    }
    const within = Number(params.get("deadline_within"));
    if (Number.isFinite(within) && within > 0) query.deadline_within = within;
    const sort = params.get("sort");
    if (sort === "match" || sort === "deadline" || sort === "newest") {
      query.sort = sort;
    }
    return { name: "search", query };
  }

  return { name: "home" };
}

export function routeToSearch(route: Route): string {
  const params = new URLSearchParams();
  switch (route.name) {
    case "home":
      return "";
    case "onboarding":
      params.set("screen", "onboarding");
      break;
    case "saved":
      params.set("screen", "saved");
      break;
    case "detail":
      params.set("screen", "opp");
      params.set("id", String(route.id));
      if (route.source) params.set("source", route.source);
      break;
    case "search": {
      params.set("screen", "search");
      for (const [key, value] of Object.entries(route.query)) {
        if (value === undefined || value === null || value === "") continue;
        if (value === false) continue;
        params.set(key, String(value));
      }
      break;
    }
  }
  const text = params.toString();
  return text ? `?${text}` : "";
}

const EVENT = "grantstudy:navigate";

let hasInAppHistory = false;
export type NavigationType = "push" | "replace" | "pop";
let lastNavType: NavigationType = "push";

export function getLastNavigationType(): NavigationType {
  return lastNavType;
}

const scrollPositions = new Map<string, number>();

export function getRouteKey(route: Route): string {
  return routeToSearch(route) || "home";
}

export function saveScrollPos(key: string, y: number): void {
  scrollPositions.set(key, y);
}

export function getSavedScrollPos(key: string): number | undefined {
  return scrollPositions.get(key);
}

export function navigate(route: Route, options: { replace?: boolean } = {}): void {
  // Save current scroll before navigating
  const currentKey = getRouteKey(parseRoute(window.location.search));
  saveScrollPos(currentKey, window.scrollY || document.documentElement.scrollTop || 0);

  const url = `${window.location.pathname}${routeToSearch(route)}`;
  if (options.replace) {
    lastNavType = "replace";
    window.history.replaceState({ inApp: false }, "", url);
  } else {
    lastNavType = "push";
    hasInAppHistory = true;
    window.history.pushState({ inApp: true }, "", url);
  }
  window.dispatchEvent(new Event(EVENT));
}

export function goBack(): void {
  // Save current scroll before going back
  const currentKey = getRouteKey(parseRoute(window.location.search));
  saveScrollPos(currentKey, window.scrollY || document.documentElement.scrollTop || 0);

  lastNavType = "pop";
  if (hasInAppHistory && window.history.state?.inApp) {
    window.history.back();
  } else {
    hasInAppHistory = false;
    navigate({ name: "home" });
  }
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    parseRoute(window.location.search),
  );

  useEffect(() => {
    const update = (e: Event) => {
      if (e.type === "popstate") {
        lastNavType = "pop";
      }
      setRoute(parseRoute(window.location.search));
    };
    window.addEventListener("popstate", update);
    window.addEventListener(EVENT, update);
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener(EVENT, update);
    };
  }, []);

  return route;
}
